import React, { useMemo } from "react";

const VersionTimelineItem = ({ version, isCurrent, promptLabel }) => {
  const date = new Date(version.created_at);
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1 ${isCurrent ? "bg-indigo-600 ring-2 ring-indigo-200" : "bg-gray-300"}`} />
        <div className="w-px flex-1 bg-gray-200 mt-1" />
      </div>
      <div className="pb-4 min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">
            {promptLabel} v{version.version_number}
          </span>
          {isCurrent && (
            <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">LIVE</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
          <span>{date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          <span>&middot;</span>
          <span>{date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>
          {version.created_by && (
            <>
              <span>&middot;</span>
              <span>{version.created_by}</span>
            </>
          )}
          <span>&middot;</span>
          <span>{version.prompt_text?.length || 0} chars</span>
        </div>
      </div>
    </div>
  );
};

const HistoryTab = ({ template, websiteMap }) => {
  const allVersions = useMemo(() => {
    const versions = [];

    if (template.base_prompt?.versions) {
      template.base_prompt.versions.forEach((v) => {
        versions.push({
          ...v,
          promptLabel: "Base Prompt",
          promptId: template.base_prompt.prompt_id,
          isCurrent: v.prompt_version_id === template.base_prompt.current_version_id,
        });
      });
    }

    (template.website_prompts || []).forEach((wp) => {
      const name = websiteMap?.[wp.website_id] || wp.website_id;
      (wp.versions || []).forEach((v) => {
        versions.push({
          ...v,
          promptLabel: name,
          promptId: wp.prompt_id,
          isCurrent: v.prompt_version_id === wp.current_version_id,
        });
      });
    });

    versions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return versions;
  }, [template, websiteMap]);

  const createdDate = template.created_at ? new Date(template.created_at) : null;
  const updatedDate = template.updated_at ? new Date(template.updated_at) : null;
  const totalPromptVersions = allVersions.length;
  const tcContainers = template.test_cases_containers || [];
  const totalTcVersions = tcContainers.reduce((sum, c) => sum + (c.versions_count || 1), 0);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">History & Activity</h3>
        <p className="text-sm text-gray-500 mt-0.5">Version timeline and template metadata</p>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border p-3">
          <span className="text-xs text-gray-500">Created</span>
          <p className="text-sm font-medium text-gray-900 mt-0.5">
            {createdDate ? createdDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Unknown"}
          </p>
        </div>
        <div className="bg-white rounded-lg border p-3">
          <span className="text-xs text-gray-500">Last Updated</span>
          <p className="text-sm font-medium text-gray-900 mt-0.5">
            {updatedDate ? updatedDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Unknown"}
          </p>
        </div>
        <div className="bg-white rounded-lg border p-3">
          <span className="text-xs text-gray-500">Prompt Versions</span>
          <p className="text-sm font-medium text-gray-900 mt-0.5">{totalPromptVersions}</p>
        </div>
        <div className="bg-white rounded-lg border p-3">
          <span className="text-xs text-gray-500">Test Case Versions</span>
          <p className="text-sm font-medium text-gray-900 mt-0.5">{totalTcVersions}</p>
        </div>
      </div>

      {/* Traceability info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-900">Scan Traceability</h4>
            <p className="text-sm text-blue-700 mt-1">
              New scans launched with this template record the exact prompt version used.
              Query scans with <code className="px-1 py-0.5 bg-blue-100 rounded text-xs font-mono">scan_template_id = '{template.scan_template_id}'</code> to
              find all scans that used this template, along with their <code className="px-1 py-0.5 bg-blue-100 rounded text-xs font-mono">base_prompt_version_id</code> and <code className="px-1 py-0.5 bg-blue-100 rounded text-xs font-mono">website_prompt_version_id</code>.
            </p>
          </div>
        </div>
      </div>

      {/* Version timeline */}
      {allVersions.length > 0 ? (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Prompt Version Timeline</h4>
          <div className="bg-white rounded-lg border p-4">
            {allVersions.map((version, i) => (
              <VersionTimelineItem
                key={version.prompt_version_id || i}
                version={version}
                isCurrent={version.isCurrent}
                promptLabel={version.promptLabel}
              />
            ))}
            {/* Timeline end marker */}
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full bg-gray-200 flex-shrink-0 mt-1" />
              </div>
              <div className="min-w-0">
                <span className="text-xs text-gray-400">Template created</span>
                {createdDate && (
                  <span className="text-xs text-gray-400 ml-2">
                    {createdDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-8 text-center text-gray-400 text-sm border-2 border-dashed rounded-lg">
          No prompt versions to display
        </div>
      )}

      {/* Template ID for reference */}
      <div className="text-xs text-gray-400 flex items-center gap-2">
        <span>Template ID:</span>
        <code className="px-2 py-1 bg-gray-100 rounded font-mono select-all">{template.scan_template_id}</code>
      </div>
    </div>
  );
};

export default HistoryTab;
